import { duration, formatDate, getCalendarMonthRange, now } from "common";
import { ManipulateType } from "dayjs";
import { useMemo } from "react";
import { useQueries, UseQueryOptions } from "react-query";
import { Package, Timeslot } from "types";
import { generateQueryKey } from "./helper";
import { useInfiniteCollection } from "./useInfiniteCollection";
import { api } from "@/api/api";
import { getProductIdsInPackage, isPartnerPackage, isVariableDemographicsPackage } from "@/models/Package";
import { Resource, Response } from "@/types/Response";

const DEFAULT_UPCOMING_FETCH: {
    count: number;
    unit: ManipulateType;
} = {
    count: 1,
    unit: "month",
};
export const usePackageTimeslots = (pkg: Package) => {
    const packageId = pkg.id;
    const experienceIds = getProductIdsInPackage(pkg);
    const isPartner = isPartnerPackage(pkg);
    const hasVariableDemographics = isVariableDemographicsPackage(pkg);

    let { start, end } = getCalendarMonthRange(now(), DEFAULT_UPCOMING_FETCH);
    if (start.isBefore(now())) {
        start = now();
    }

    const {
        data: mainTimeslots,
        isLoading: isMainLoading,
        isFetching: isMainFetching,
    } = useInfiniteCollection<Timeslot>(
        "timeslots",
        {
            product: experienceIds.join(","),
            ...(!isPartner && { package: packageId }),
            start: [formatDate(start), formatDate(end)].join(","),
            "capacity.open[gt]": 0,
        },
        { keepPreviousData: true, staleTime: duration(15, "seconds") },
    );

    const hasAllExperiencesCovered = useMemo(
        () => experienceIds.every((expId) => mainTimeslots.some((slot) => slot.product === expId)),
        [experienceIds, mainTimeslots],
    );

    const isOnGoing = isMainFetching || isMainLoading;
    const upcomingStartDate = formatDate(start.startOf("month"));
    const upcomingDatesQueries = useQueries(
        experienceIds.map((experienceId) => ({
            queryKey: ["timeslots", "collection", "upcoming", experienceId, upcomingStartDate],
            queryFn: async () => {
                // We only want 1 date for each experience to know the earliest upcoming date
                const params = upcomingStartDate ? { limit: 1, startDate: upcomingStartDate } : { limit: 1 };
                const { data } = await api.get<string[]>(`/experiences/${experienceId}/upcoming`, { params });
                return data;
            },
            enabled: hasVariableDemographics && !hasAllExperiencesCovered && !isOnGoing,
            staleTime: duration(15, "seconds"),
            keepPreviousData: true,
        })),
    );

    const isUpcomingLoading = upcomingDatesQueries.some((q) => q.isLoading);
    const isUpcomingFetching = upcomingDatesQueries.some((q) => q.isFetching);

    const shouldFetchMissingExperiences = !hasAllExperiencesCovered && !isUpcomingLoading && !isUpcomingFetching;

    const missingExperienceTimeslotsQueriesData = shouldFetchMissingExperiences
        ? experienceIds
              .map<UseQueryOptions<Timeslot[], unknown, Timeslot[]> | null>((experienceId, index) => {
                  // Skip if we already have timeslots for this experience
                  if (mainTimeslots.some((slot) => slot.product === experienceId)) {
                      return null;
                  }

                  const queryResult = upcomingDatesQueries[index];
                  // If the upcoming query isn't finished, errored out, or has no dates, skip it
                  if (!queryResult?.data || queryResult.data.length === 0) {
                      return null;
                  }

                  // Found at least one upcoming date
                  const earliestDateString = queryResult.data[0];
                  const { start: expStart, end: expEnd } = getCalendarMonthRange(
                      now(earliestDateString),
                      DEFAULT_UPCOMING_FETCH,
                  );

                  const params = {
                      product: experienceId,
                      ...(!isPartner && { package: packageId }),
                      start: [formatDate(expStart), formatDate(expEnd)].join(","),
                      "capacity.open[gt]": 0,
                  };

                  const queryKey = generateQueryKey(["timeslots", "collection", "infinite", experienceId], params);
                  // Query definition to fetch timeslots for the next 1 month from earliestDate
                  return {
                      queryKey: queryKey,
                      queryFn: async () => {
                          const { data } = await api.get<Response<Timeslot, Resource>>(`/timeslots`, { params });
                          return data.data;
                      },
                      enabled: shouldFetchMissingExperiences,
                      keepPreviousData: true,
                  };
              })
              .filter((item): item is UseQueryOptions<Timeslot[], unknown, Timeslot[]> => item !== null)
        : [];

    const missingTimeslotsQueries = useQueries(missingExperienceTimeslotsQueriesData);
    const isMissingTimeslotsLoading = missingTimeslotsQueries.some((q) => q.isLoading);
    const isMissingTimeslotsFetching = missingTimeslotsQueries.some((q) => q.isFetching);

    const missingTimeslots = useMemo<Timeslot[]>(() => {
        return missingTimeslotsQueries.flatMap((q) => q.data ?? []);
    }, [missingTimeslotsQueries]);

    return {
        data: [...mainTimeslots, ...missingTimeslots],
        isFetching: isMainFetching || isUpcomingFetching || isMissingTimeslotsFetching,
        isLoading: isMainLoading || isUpcomingLoading || isMissingTimeslotsLoading,
    };
};
